import {
  ReactElement,
  SyntheticEvent,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { Stack, DialogContentText, Alert } from '@mui/material';
import { useFormikContext } from 'formik';

// eslint-disable-next-line max-len
import { AdminIntegrationStockLocationExtended } from '~services/admin/integration/stockLocation/types';
import CustomAutocomplete from '~components/CustomAutocomplete';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';
import { RoutesActionFilterChip } from '~app/modules/Routes/styles';
import { hasError } from '~utils/formHelpers';

import { useDialogIntegrationAssignWarehousesContext } from '../../Context';
import {
  FIELDS_NAME,
  getInitialValues,
  getDefaultStockLocations,
} from '../../utils';

const DialogIntegrationAssignWarehousesSelection = (): ReactElement => {
  const { stockLocations } = useDialogIntegrationAssignWarehousesContext();

  const { errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<ReturnType<typeof getInitialValues>>();

  const refStockLocationsAutocomplete =
    useRef<CustomAutocompleteRef<AdminIntegrationStockLocationExtended, true>>(
      null,
    );

  const setDefaultStockLocations = useCallback(() => {
    const defaultStockLocations = getDefaultStockLocations(stockLocations);

    if (defaultStockLocations.length) {
      refStockLocationsAutocomplete.current?.setValue(defaultStockLocations);
    }
  }, [stockLocations]);

  useEffect(() => {
    setDefaultStockLocations();
  }, [setDefaultStockLocations]);

  const handleChangeStockLocations = useCallback(
    (
      _event: SyntheticEvent,
      newStockLocations: AdminIntegrationStockLocationExtended[],
    ) => {
      const newNetworkNodeIds = newStockLocations.map(
        (stockLocation) => stockLocation.networkNodeId,
      );

      setFieldValue(FIELDS_NAME.NETWORK_NODE_IDS, newNetworkNodeIds);
    },
    [setFieldValue],
  );

  const handleBlurStockLocations = useCallback(() => {
    setFieldTouched(FIELDS_NAME.NETWORK_NODE_IDS, true);
  }, [setFieldTouched]);

  return (
    <Stack spacing={2}>
      <DialogContentText variant="caption" color="text.secondary">
        Seleccione los depósitos desde los cuales se podrán importar pedidos.
      </DialogContentText>

      <Stack spacing={2}>
        <CustomAutocomplete
          innerRef={refStockLocationsAutocomplete}
          multiple
          disableCloseOnSelect
          filterSelectedOptions
          limitTags={4}
          renderTags={(vals, getTagsProps) =>
            vals.map((val, index) => (
              // eslint-disable-next-line react/jsx-key
              <RoutesActionFilterChip
                label={val.description}
                size="small"
                {...getTagsProps({ index })}
              />
            ))
          }
          disableClearable
          options={stockLocations}
          label="Seleccionar depósitos"
          name={FIELDS_NAME.NETWORK_NODE_IDS}
          onChange={handleChangeStockLocations}
          onBlur={handleBlurStockLocations}
          error={hasError(touched, errors, FIELDS_NAME.NETWORK_NODE_IDS)}
          getOptionLabel={(option) => option.description}
        />

        <Alert
          severity="info"
          variant="standard"
          icon={false}
          sx={(currentTheme) => ({
            border: `1px solid ${currentTheme.palette.info.light}`,
            ...currentTheme.typography.caption,
          })}
        >
          Si no se selecciona ningún depósito, el sistema importará desde
          cualquier origen.
        </Alert>
      </Stack>
    </Stack>
  );
};

export default DialogIntegrationAssignWarehousesSelection;
